import { BehaviorSubject } from 'rxjs';
import ResponseHandlerService from './ResponseHandlerService';
import CoreApi from '../api/core';
import ClientApi from 'api/client';
import { USER_ROLE_TYPE } from 'constants/general';
import {
  isRoleType,
  handleUserResultForCalendarSettings,
  actionByRole,
} from 'lib/helpers';
import { ROLES_OBJECT } from '../constants/onBoarding';
import { IUser } from 'interfaces';
import { requestObject } from '../lib/utils';
import { RESOURCES } from '../constants/general';
import { LOCAL_USER_TYPE } from 'constants/localStorage';
import teacherEndpoints from '../api/resources/teacher';
import staffEndpoints from '../api/resources/staff';
import directorEndpoints from '../api/resources/director';
import userEndpoints from 'api/resources/user';

export const DEFAULT_USER: IUser = { email: '' };

class UserService {
  static instance: UserService;
  private user$: BehaviorSubject<IUser>;
  private respHandler = ResponseHandlerService.getInstance();

  constructor() {
    this.user$ = new BehaviorSubject<IUser>(DEFAULT_USER);
  }

  static getInstance(): UserService {
    if (!UserService.instance) UserService.instance = new UserService();
    return UserService.instance;
  }

  /* Setters */

  setUser = (_user: any) => this.user$.next(_user);

  /* Getters */

  getUser = () => this.user$;

  /* CREATES */

  addTeacher = async (teachers: any[], centerId?: number) =>
    this.respHandler.handleResponse({
      promise: actionByRole(LOCAL_USER_TYPE(), {
        owner: () => teacherEndpoints.postTeacherAsOwnerByCenterId(centerId, requestObject(RESOURCES.CENTERS, teachers, RESOURCES.TEACHER)),
        admin: () => teacherEndpoints.postTeacherAsAdminByCenterId(centerId, requestObject(RESOURCES.CENTERS, teachers, RESOURCES.TEACHER)),
        director: () => teacherEndpoints.postTeacherAsDirectorByCenterId(centerId, { teacher: { ...teachers[0] } }),
        teacher: () => teacherEndpoints.postTeacherAsShared({ teacher: { ...teachers[0] } }),
      }),
    });


  moveDirectors = (body: any, centerId: number) =>
    this.respHandler.handleResponse({
      promise: actionByRole(LOCAL_USER_TYPE(), {
        owner: () => directorEndpoints.putMoveDirectors(body, centerId),
        admin: () => directorEndpoints.putMoveDirectors(body, centerId),
        director: () => directorEndpoints.putMoveDirectors(body, centerId),
        teacher: () => directorEndpoints.putMoveDirectors(body, centerId),
      }),
    });

  moveTeachers = (body: any, centerId: number) =>
    this.respHandler.handleResponse({
      promise: actionByRole(LOCAL_USER_TYPE(), {
        owner: () => teacherEndpoints.putMoveTeachers(body, centerId),
        admin: () => teacherEndpoints.putMoveTeachers(body, centerId),
        director: () => teacherEndpoints.putMoveTeachers(body, centerId),
        teacher: () => teacherEndpoints.putMoveTeachers(body, centerId),
      }),
    });

  addStaff = async (staff: any) =>
    this.respHandler.handleResponse({
      promise: actionByRole(LOCAL_USER_TYPE(), {
        owner: () => staffEndpoints.postStaff(staff),
        admin: () => staffEndpoints.postStaff(staff),
        director: () => staffEndpoints.postStaff(staff),
        teacher: () => staffEndpoints.postStaff(staff),
      }),
      successCallback: (res: any) => {
        return res;
      },
    });

  /* READS */

  fetchUser = (params?: any) =>
    this.respHandler.handleResponse({
      promise: userEndpoints.getUser(params),
      successCallback: (res: any) => {
        handleUserResultForCalendarSettings(res.user);
        localStorage.setItem('user_name', res.user.name);
        localStorage.setItem('user_lastname', res.user.lastname);
        localStorage.setItem('user_id', res.user.id);
        return this.setUser({ ...this.user$.getValue(), ...res.user });
      },
    });

  fetchTeacher = () =>
    this.respHandler.handleResponse({
      promise: teacherEndpoints.getTeacher(),
      successCallback: (res: any) => {
        if (res && res.user.email === localStorage.getItem('user_email')) {
          localStorage.setItem('user_name', res.user.name);
          localStorage.setItem('user_lastname', res.user.lastname);
          localStorage.setItem('user_id', res.user.id);
        }
        return this.setUser({ ...this.user$.getValue(), ...res.user });
      },
    });

  fetchStaffList = async (body: any) =>
    actionByRole(LOCAL_USER_TYPE(), {
      owner: () => staffEndpoints.getStaff(body),
      admin: () => staffEndpoints.getStaff(body),
      director: () => staffEndpoints.getStaff(body),
      teacher: () => staffEndpoints.getStaff(body),
    })

  fetchStaffListQuery = async (page: number, query: string) =>
    this.respHandler.handleResponse({
      promise: actionByRole(LOCAL_USER_TYPE(), {
        owner: () => staffEndpoints.searchStaff(page, query),
        admin: () => staffEndpoints.searchStaff(page, query),
        director: () => staffEndpoints.searchStaff(page, query),
        teacher: () => staffEndpoints.searchStaff(page, query),
      }),
      successCallback: (res: any) => {
        return res;
      },
    });

  fetchStaffDetail = async (id: number, centerId?: number) =>
    this.respHandler.handleResponse({
      promise: actionByRole(LOCAL_USER_TYPE(), {
        owner: () => staffEndpoints.showStaffDetail(id),
        admin: () => staffEndpoints.showStaffDetail(id),
        director: () => staffEndpoints.showStaffDetail(id),
        teacher: () => directorEndpoints.getDirectorDetailAsTeacher(id, centerId || 0),
      }),
      successCallback: (res: any) => {
        return res;
      },
    });

  fetchCenterTeachers = async () => {
    try {
      const teacher = await teacherEndpoints.geCenterTeachers();
      return teacher;
    } catch (error) {
      return error;
    }
  };

  checkEmail = (email: string) => staffEndpoints.checkEmail(encodeURIComponent(email));

  /* UPDATES */

  updateUser = (data: any) =>
    ClientApi.organizationScope[USER_ROLE_TYPE].putUser(data).then(() =>
      this.fetchUser(),
    );

  editTeacher = async (teacher: any[], centerId: number, teacherId: number) =>
    this.respHandler.handleResponse({
      promise: actionByRole(LOCAL_USER_TYPE(), {
        owner: () =>
          teacherEndpoints.putTeacherByCenterId(
            { teacher: { ...teacher } },
            centerId,
            teacherId,
          ),
        admin: () =>
          teacherEndpoints.putTeacherByCenterId(
            { teacher: { ...teacher } },
            centerId,
            teacherId,
          ),
        director: () =>
          teacherEndpoints.putTeacherByCenterId(
            { teacher: { ...teacher } },
            centerId,
            teacherId,
          ),
        teacher: () =>
          teacherEndpoints.putTeacherByCenterIdAsTeacher(
            { teacher: { ...teacher } },
            centerId,
            teacherId,
          ),
      }),
    });

  /* DELETES */

  deleteTeacher = (centerId: number, teacherId: number) =>
    this.respHandler.handleResponse({
      promise: isRoleType([ROLES_OBJECT.TEACHER.value])
        ? CoreApi.centerScope.deleteTeacher(teacherId)
        : ClientApi.organizationScope[USER_ROLE_TYPE].deleteTeacher(
            centerId,
            teacherId,
          ),
    });

  deleteStaff = async (id: number) =>
    this.respHandler.handleResponse({
      promise: actionByRole(LOCAL_USER_TYPE(), {
        owner: () => staffEndpoints.deleteStaff(id),
        admin: () => staffEndpoints.deleteStaff(id),
        director: () => staffEndpoints.deleteStaff(id),
        teacher: () => staffEndpoints.deleteStaff(id),
      }),
      successCallback: (res: any) => {
        return res;
      },
    });

  reinvite = async (email: string) =>
    actionByRole(LOCAL_USER_TYPE(), {
      owner: () => userEndpoints.reinvite({ email }),
      admin: () => userEndpoints.reinvite({ email }),
      director: () => userEndpoints.reinvite({ email }),
      teacher: () => userEndpoints.reinvite({ email }),
    });
}

export default UserService;
